import {
    REALTIME_TRANSCRIPTION_PATH,
    RealtimeTranscriptionCloseCode,
    RealtimeTranscriptionErrorCode,
} from 'chaika-enums';
import { RealtimeTranscriptionServerMessage } from 'chaika-frontend-types/frontend-cms';

export interface RealtimeTranscriptionCallbacks {
    onPartial(utteranceId: string, text: string): void;
    onCommitted(utteranceId: string, text: string): void;
    onUtteranceFailed(utteranceId: string): void;
    /** Fired once when the server ends the session; not fired after an intentional close(). */
    onClosed(errorCode: RealtimeTranscriptionErrorCode | null): void;
}

const CONNECT_TIMEOUT_MS = 10000;
const STALL_CHECK_INTERVAL_MS = 2000;
// audio streams constantly while dictating, so a socket holding this much unsent audio is dead
const STALL_BUFFER_SECONDS = 3;

const ERROR_CODE_BY_CLOSE_CODE: Record<number, RealtimeTranscriptionErrorCode> = {
    [RealtimeTranscriptionCloseCode.ProviderConnectFailed]: RealtimeTranscriptionErrorCode.ProviderConnectFailed,
    [RealtimeTranscriptionCloseCode.ProviderStalled]: RealtimeTranscriptionErrorCode.ProviderStalled,
    [RealtimeTranscriptionCloseCode.ProviderError]: RealtimeTranscriptionErrorCode.ProviderError,
    [RealtimeTranscriptionCloseCode.Capacity]: RealtimeTranscriptionErrorCode.Capacity,
    [RealtimeTranscriptionCloseCode.ProtocolViolation]: RealtimeTranscriptionErrorCode.ProtocolViolation,
    [RealtimeTranscriptionCloseCode.SessionExpired]: RealtimeTranscriptionErrorCode.SessionExpired,
    [RealtimeTranscriptionCloseCode.IdleTimeout]: RealtimeTranscriptionErrorCode.IdleTimeout,
    [RealtimeTranscriptionCloseCode.Internal]: RealtimeTranscriptionErrorCode.Internal,
    1001: RealtimeTranscriptionErrorCode.ServerShutdown,
};

export class RealtimeTranscriptionClient {
    private ws: WebSocket | null = null;
    private ready = false;
    private intentionallyClosed = false;
    private receivedErrorCode: RealtimeTranscriptionErrorCode | null = null;
    private stallTimer: ReturnType<typeof setInterval> | null = null;

    constructor(private readonly callbacks: RealtimeTranscriptionCallbacks) {}

    /** Opens the socket and resolves with the sample rate once the server reports session-ready. */
    public connect(): Promise<{ sampleRate: number }> {
        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const url = `${protocol}://${window.location.host}${REALTIME_TRANSCRIPTION_PATH}`;

        return new Promise((resolve, reject) => {
            const ws = new WebSocket(url);
            this.ws = ws;

            const connectTimeout = setTimeout(() => {
                this.close();
                reject(new Error('Transcription socket timed out waiting for session-ready'));
            }, CONNECT_TIMEOUT_MS);

            ws.onmessage = event => {
                const message = this.parseMessage(event.data);
                if (!message) {
                    return;
                }
                if (message.type === 'session-ready') {
                    clearTimeout(connectTimeout);
                    this.ready = true;
                    this.startStallWatchdog(message.sampleRate);
                    resolve({ sampleRate: message.sampleRate });

                    return;
                }
                this.handleMessage(message);
            };

            ws.onclose = event => {
                this.stopStallWatchdog();
                if (this.intentionallyClosed) {
                    return;
                }
                clearTimeout(connectTimeout);
                const errorCode = this.receivedErrorCode ?? ERROR_CODE_BY_CLOSE_CODE[event.code] ?? null;
                if (!this.ready) {
                    reject(new Error(`Transcription socket closed before ready: ${errorCode ?? event.code}`));

                    return;
                }
                this.callbacks.onClosed(errorCode);
            };
        });
    }

    public sendAudio(frame: ArrayBuffer): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(frame);
        }
    }

    public close(): void {
        this.stopStallWatchdog();
        this.intentionallyClosed = true;
        if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
            this.ws.close(1000);
        }
        this.ws = null;
    }

    // unsent audio piling up in the socket is the only client-side liveness signal (WS pings are invisible to the browser)
    private startStallWatchdog(sampleRate: number): void {
        const thresholdBytes = sampleRate * 2 * STALL_BUFFER_SECONDS;
        this.stallTimer = setInterval(() => {
            if (this.ws && this.ws.bufferedAmount > thresholdBytes) {
                this.close();
                this.callbacks.onClosed(null);
            }
        }, STALL_CHECK_INTERVAL_MS);
    }

    private stopStallWatchdog(): void {
        if (this.stallTimer !== null) {
            clearInterval(this.stallTimer);
            this.stallTimer = null;
        }
    }

    private handleMessage(message: RealtimeTranscriptionServerMessage): void {
        switch (message.type) {
            case 'partial':
                this.callbacks.onPartial(message.utteranceId, message.text);
                break;
            case 'committed':
                this.callbacks.onCommitted(message.utteranceId, message.text);
                break;
            case 'utterance-failed':
                this.callbacks.onUtteranceFailed(message.utteranceId);
                break;
            case 'error':
                // the server closes the socket right after; remember the code for onclose
                this.receivedErrorCode = message.code;
                break;
        }
    }

    private parseMessage(data: unknown): RealtimeTranscriptionServerMessage | null {
        if (typeof data !== 'string') {
            return null;
        }
        try {
            return JSON.parse(data) as RealtimeTranscriptionServerMessage;
        } catch {
            return null;
        }
    }
}
